/**
 * Couple-side PostHog event registry for /me (Homebase + couple onboarding).
 *
 * Source of truth: "Dova Metrics & KPIs" (Notion), §4.4 and §5. Names are
 * `couple_*`, snake_case, object_verb_past_tense. Capture ONLY through
 * `track()` in ./track.ts — the property map below is the allowlist, so an
 * event cannot carry a property that is not declared here.
 *
 * Never add: names, email, phone, free-text answers, mood/feeling values,
 * ratings, report content, or any token. Counts and booleans only (§7).
 *
 * `app`, `couple_id` and `therapist_id` are registered as super
 * properties by useMeAnalyticsIdentity, so they ride on every event without
 * being listed per event.
 */

export const COUPLE_EVENTS = {
  // Family E — activation (couple onboarding)
  inviteOpened: "couple_invite_opened",
  onboardingStepViewed: "couple_onboarding_step_viewed",
  onboardingStepCompleted: "couple_onboarding_step_completed",
  onboardingCompleted: "couple_onboarding_completed",

  // Homebase
  homebaseViewed: "couple_homebase_viewed",
  sessionJoinClicked: "couple_session_join_clicked",
  // Distinct from the server's therapist_session_canceled (§1265) by design:
  // this is "the couple submitted a cancel through this UI," the server
  // event is "a session was canceled, however it happened." The delta
  // between the two is the diagnostic — client-without-server means this
  // cancel never reached/completed on the server; keep the names apart so
  // neither channel silently double-counts or masks the other.
  sessionCanceled: "couple_session_cancel_submitted",
  rescheduleRequested: "couple_reschedule_requested",
  rescheduleAccepted: "couple_reschedule_accepted",
  reportViewed: "couple_report_viewed",

  // Family G — self-report
  checkinStarted: "couple_checkin_started",
  checkinSubmitted: "couple_checkin_submitted",
  reflectionStarted: "couple_reflection_started",
  reflectionSubmitted: "couple_reflection_submitted",
} as const;

export type CoupleEventName = (typeof COUPLE_EVENTS)[keyof typeof COUPLE_EVENTS];

interface SessionProps {
  session_id: string;
  /** Omitted where the client read does not carry it (feedback, pick page). */
  is_standing?: boolean;
}

export interface CoupleEventProperties {
  couple_invite_opened: Record<string, never>;
  couple_onboarding_step_viewed: { step: string; step_index: number; steps_total: number };
  couple_onboarding_step_completed: { step: string; step_index: number; steps_total: number };
  couple_onboarding_completed: { steps_total: number };

  couple_homebase_viewed: {
    has_next_session: boolean;
    next_session_is_standing: boolean;
    partner_joined: boolean;
    past_sessions_total: number;
    has_unread_report: boolean;
    pending_reschedule_offers: number;
  };
  couple_session_join_clicked: SessionProps & { embedded: boolean };
  couple_session_cancel_submitted: SessionProps;
  couple_reschedule_requested: SessionProps & { slots_offered: number };
  couple_reschedule_accepted: { offer_id: string; session_id: string };
  couple_report_viewed: { report_id: string; is_first_view: boolean };

  couple_checkin_started: SessionProps & { is_edit: boolean };
  couple_checkin_submitted: SessionProps & { questions_answered: number; is_edit: boolean };
  couple_reflection_started: SessionProps & { is_edit: boolean };
  couple_reflection_submitted: SessionProps & { questions_answered: number; is_edit: boolean };
}
